/**
 * Sidebar navigation with plugin route injection
 */

import { clsx } from 'clsx';
import { useState, useRef, useEffect, useMemo } from 'react';
import { Home, Search, MessageSquare, FileText, Database, Layers, Zap, Package, Users, Shield, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { NavItem } from '../../types/plugins';
import { pluginRegistry } from '../../plugins/PluginRegistry';
import { usePermissions } from '../../hooks/usePermissions';
import { useConfig } from '../../contexts/ConfigContext';

interface NavItemButtonProps {
  item: NavItem;
  Icon: any;
  isActive: boolean;
  collapsed: boolean;
  showTooltip: boolean;
  isPlugin?: boolean;
  onNavigate?: (path: string) => void;
  onToggle?: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

function NavItemButton({ 
  item, 
  Icon, 
  isActive, 
  collapsed, 
  showTooltip,
  isPlugin = false,
  onNavigate, 
  onToggle,
  onMouseEnter,
  onMouseLeave
}: NavItemButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  
  useEffect(() => {
    if (showTooltip && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 8
      });
    }
  }, [showTooltip]);
  
  return (
    <div
      className="relative"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        ref={buttonRef}
        onClick={() => {
          onNavigate?.(item.path);
          if (collapsed && onToggle) {
            onToggle();
          }
        }}
        className={clsx(
          'group w-full flex items-center text-sm font-medium transition-all duration-200 text-left border-l-2',
          collapsed ? 'justify-center px-2 py-3 rounded-md' : 'gap-3 px-4 py-3 rounded-lg',
          isActive
            ? isPlugin
              ? 'bg-slate-800/50 text-white border-l-4 border-teal-400 shadow-md'
              : 'bg-slate-800/50 text-white border-l-4 border-blue-400 shadow-md'
            : 'text-slate-300 border-l-2 border-transparent hover:bg-slate-800/30 hover:text-white hover:border-l-4 hover:border-slate-400'
        )}
      >
        {Icon && <Icon className={clsx(
          'transition-all flex-shrink-0',
          collapsed ? 'h-6 w-6' : 'h-5 w-5',
          isActive 
            ? 'text-white' 
            : isPlugin 
              ? 'text-slate-400 dark:text-slate-300 group-hover:text-teal-300' 
              : 'text-slate-400 dark:text-slate-300 group-hover:text-blue-300'
        )} />}
        {!collapsed && (
          <>
            <span>{item.label}</span>
            {item.badge && (
              <span className={clsx(
                "ml-auto text-white px-2 py-0.5 rounded text-xs font-semibold",
                isPlugin ? "bg-teal-600" : "bg-blue-600"
              )}>
                {item.badge}
              </span>
            )}
            {isActive && (
              <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full" />
            )}
          </>
        )}
      </button>
      
      {/* Tooltip for collapsed state - rendered with fixed positioning */}
      {showTooltip && (
        <div 
          className="fixed z-[9999] px-3 py-2 bg-slate-800 text-white text-sm font-medium rounded-md shadow-lg whitespace-nowrap pointer-events-none border border-slate-600"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateY(-50%)'
          }}
        >
          {item.label}
          {item.badge && (
            <span className={clsx(
              "ml-2 text-white px-2 py-0.5 rounded text-xs font-semibold",
              isPlugin ? "bg-teal-600" : "bg-blue-600"
            )}>
              {item.badge}
            </span>
          )}
          {/* Arrow pointing to the button */}
          <div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-slate-800"></div>
        </div>
      )}
    </div>
  );
}

export interface SidebarProps {
  pluginItems?: NavItem[];
  onNavigate?: (path: string) => void;
  currentPath?: string;
  collapsed?: boolean;
  onToggle?: () => void;
  // Branding props
  logo?: string;
  logoAlt?: string;
  platformTitle?: string;
  platformSubtitle?: string;
}

const RFP_EXTERNAL_DATA_PATH = '/rfp/external-data';

export function Sidebar({ 
  pluginItems = [], 
  onNavigate, 
  currentPath = '', 
  collapsed = false, 
  onToggle,
  logo = '/logo.svg',
  logoAlt = 'Logo',
  platformTitle = 'TraceKit',
  platformSubtitle = 'Evidence Platform'
}: SidebarProps) {
  const config = useConfig();
  const branding = config?.branding;
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const opportunitiesLabel = config.customization?.opportunitiesDashboardLabel;
  const dataPipelineOpenAccess =
    config?.customization?.featureFlags?.dataPipelineOpenAccess === true;
  
  // Plugins that replace core navigation items (scalable for future plugins)
  const PLUGINS_THAT_REPLACE_REPORTS = ['rfp-agent', 'hr-assistant'];
  const PLUGINS_THAT_REPLACE_DATA_MANAGEMENT = ['rfp-agent'];
  
  // Check if any plugin that replaces reports is active
  const hasReportsReplacement = PLUGINS_THAT_REPLACE_REPORTS.some(pluginId => 
    pluginRegistry.getPlugin(pluginId) !== undefined
  );
  
  // Check if any plugin that replaces data management is active
  const hasDataManagementReplacement = PLUGINS_THAT_REPLACE_DATA_MANAGEMENT.some(pluginId => 
    pluginRegistry.getPlugin(pluginId) !== undefined
  );

  // Permission mapping for navigation items
  const getItemPermission = (path: string): string | null => {
    if (path === '/data') return dataPipelineOpenAccess ? null : 'data_management.read';
    if (path === '/reports') return 'reports.read';
    // Search and Home are always accessible (core features)
    if (path === '/search') return null;
    // Home is always accessible
    return null;
  };
  const canAccessPermission = (permission?: string | string[]): boolean => {
    if (!permission) return true;
    if (permissionsLoading) return false;
    if (Array.isArray(permission)) return permission.some((perm) => hasPermission(perm));
    return hasPermission(permission);
  };

  // Main nav: Home, AI Search, Reports. Data Pipeline rendered at bottom (scalable for all builds).
  const coreMainItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [
      { label: 'Home', icon: Home, path: '/' },
      { label: 'AI Search', icon: Search, path: '/search' },
      ...(hasReportsReplacement ? [] : [{ label: 'Reports', icon: FileText, path: '/reports' }]),
    ];
    return items.filter(item => {
      const requiredPermission = getItemPermission(item.path);
      return canAccessPermission(requiredPermission ?? undefined);
    });
  }, [canAccessPermission, hasReportsReplacement, dataPipelineOpenAccess]);

  // Footer nav: Data Pipeline at bottom (not third tab)
  const coreFooterItems: NavItem[] = useMemo(() => {
    if (hasDataManagementReplacement) return [];
    const items: NavItem[] = [{ label: 'Data Pipeline', icon: Database, path: '/data', description: 'Sources, Knowledge, Jobs, Entities, Chat' }];
    return items.filter(item => {
      const requiredPermission = getItemPermission(item.path);
      return canAccessPermission(requiredPermission ?? undefined);
    });
  }, [canAccessPermission, hasDataManagementReplacement, dataPipelineOpenAccess]);

  // Filter plugin items based on permissions; KBF-111: override nav label for Opportunities (e.g. Keystone "Opportunities Dashboard")
  const filteredPluginItems = useMemo(() => {
    let items = pluginItems.filter(item => {
      if (dataPipelineOpenAccess && item.path === '/data') {
        return true;
      }
      return canAccessPermission(item.requiredPermission as string | string[] | undefined);
    });
    if (opportunitiesLabel) {
      items = items.map(item =>
        item.path === RFP_EXTERNAL_DATA_PATH ? { ...item, label: opportunitiesLabel } : item
      );
    }
    return items;
  }, [pluginItems, canAccessPermission, opportunitiesLabel, dataPipelineOpenAccess]);

  // Single state to track which tooltip is showing (keyed by path)
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  // Fallback when logo image fails to load (e.g. missing file)
  const [logoError, setLogoError] = useState(false);
  useEffect(() => {
    setLogoError(false);
  }, [logo]);

  return (
    <div 
      className={clsx(
        "text-white h-screen flex flex-col shadow-2xl transition-all duration-300 ease-in-out relative",
        collapsed ? "w-20" : "w-64"
      )}
      style={{
        background: 'var(--color-sidebar-bg, linear-gradient(180deg, #0f172a 0%, #1e293b 50%, #0f172a 100%))'
      }}
    >
      {/* Header with gradient accent */}
      <div 
        className={clsx(
          "border-b transition-all duration-300",
          collapsed ? "p-4" : "p-6"
        )}
        style={{
          borderColor: 'var(--color-sidebar-border, rgba(255, 255, 255, 0.1))',
          background: 'var(--color-sidebar-accent, rgba(59, 130, 246, 0.2))'
        }}
      >
        <div className={clsx(
          "flex items-center transition-all duration-300",
          collapsed ? "flex-col gap-3" : "justify-between gap-3"
        )}>
          {/* Brand logo — always visible regardless of collapsed state or app theme.
              Rendered on its own solid light chip (not the gradient toggle background) so
              logos with dark/transparent artwork (e.g. GovEarner's charcoal-on-transparent
              mark) stay legible instead of blending into the sidebar accent color. */}
          <div className={clsx("flex items-center min-w-0", collapsed ? "flex-col gap-2" : "gap-3")}>
            <div
              data-testid="sidebar-logo"
              className="flex-shrink-0 h-10 w-10 rounded-lg bg-white shadow-md flex items-center justify-center p-1.5"
            >
              {logo && !logoError ? (
                <img
                  src={logo}
                  alt={logoAlt}
                  className="h-full w-full object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <Shield className="h-6 w-6 text-slate-700" />
              )}
            </div>
            {!collapsed && (
              <div className="transition-opacity duration-300 min-w-0">
                <h1 
                  className="text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent truncate"
                  style={{ color: 'var(--color-sidebar-text, #ffffff)' }}
                >
                  {platformTitle}{typeof window !== 'undefined' && (window as any).RUNTIME_CONFIG?.DEPLOYMENT_ENV === 'dev' ? ' (Dev)' : ''}
                </h1>
                <p 
                  className="text-xs font-medium"
                  style={{ color: 'var(--color-sidebar-text-secondary, #cbd5e1)' }}
                >
                  {platformSubtitle}
                </p>
              </div>
            )}
          </div>

          {/* Collapse/expand toggle — a distinct control from the brand logo, with a
              directional chevron icon so its purpose is unambiguous, and a
              semi-transparent white background/border for reliable contrast against
              any sidebar accent color (not tied to the primary gradient). */}
          <button
            type="button"
            onClick={onToggle}
            data-testid="sidebar-toggle"
            className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-md bg-white/10 text-white border border-white/30 hover:bg-white/25 hover:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 transition-colors"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
          >
            {collapsed ? (
              <ChevronsRight className="h-4 w-4" />
            ) : (
              <ChevronsLeft className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
      
      {/* Navigation with modern styling: main core, plugin items, then Data Pipeline at bottom */}
      <nav className={clsx(
        "flex-1 flex flex-col overflow-y-auto overflow-x-visible transition-all duration-300",
        collapsed ? "px-2 py-6" : "px-4 py-6"
      )}>
        <div className="space-y-2">
          {/* Core main: Home, AI Search, Reports */}
          {coreMainItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPath === item.path;
            const showTooltip = collapsed && hoveredItem === item.path;
            return (
              <NavItemButton
                key={item.path}
                item={item}
                Icon={Icon}
                isActive={isActive}
                collapsed={collapsed}
                showTooltip={showTooltip}
                isPlugin={false}
                onNavigate={onNavigate}
                onToggle={onToggle}
                onMouseEnter={() => collapsed && setHoveredItem(item.path)}
                onMouseLeave={() => setHoveredItem(null)}
              />
            );
          })}

          {/* Plugin Items (e.g. Employee Experience, Admin & Governance) */}
          {filteredPluginItems.length > 0 && (
            <>
              <div className={clsx("my-4 transition-all duration-300", collapsed ? "px-2" : "px-4")}>
                <div className="h-px bg-white/10" />
              </div>
              {filteredPluginItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentPath === item.path || currentPath.startsWith(item.path + '/');
                const showTooltip = collapsed && hoveredItem === item.path;
                return (
                  <NavItemButton
                    key={item.path}
                    item={item}
                    Icon={Icon}
                    isActive={isActive}
                    collapsed={collapsed}
                    showTooltip={showTooltip}
                    isPlugin={true}
                    onNavigate={onNavigate}
                    onToggle={onToggle}
                    onMouseEnter={() => collapsed && setHoveredItem(item.path)}
                    onMouseLeave={() => setHoveredItem(null)}
                  />
                );
              })}
            </>
          )}

          {/* Core footer: Data Pipeline at bottom */}
          {coreFooterItems.length > 0 && (
            <>
              <div className={clsx("my-4 transition-all duration-300", collapsed ? "px-2" : "px-4")}>
                <div className="h-px bg-white/10" />
              </div>
              {coreFooterItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentPath === item.path;
                const showTooltip = collapsed && hoveredItem === item.path;
                return (
                  <NavItemButton
                    key={item.path}
                    item={item}
                    Icon={Icon}
                    isActive={isActive}
                    collapsed={collapsed}
                    showTooltip={showTooltip}
                    isPlugin={false}
                    onNavigate={onNavigate}
                    onToggle={onToggle}
                    onMouseEnter={() => collapsed && setHoveredItem(item.path)}
                    onMouseLeave={() => setHoveredItem(null)}
                  />
                );
              })}
            </>
          )}
        </div>
      </nav>

      {/* Footer with branding */}
      {!collapsed && (
        <div 
          className="p-6 border-t transition-opacity duration-300"
          style={{
            borderColor: 'var(--color-sidebar-border, rgba(255, 255, 255, 0.1))',
            background: 'var(--color-sidebar-bg, linear-gradient(90deg, rgba(15, 23, 42, 0.5), rgba(30, 41, 59, 0.5)))'
          }}
        >
          {branding?.poweredBy !== false && (
            <div 
              className="text-xs text-center font-medium"
              style={{ color: 'var(--color-sidebar-text-secondary, #cbd5e1)' }}
            >
              Powered by {typeof branding?.poweredBy === 'string' ? branding.poweredBy : (platformTitle?.includes('Keystone') ? 'Keystone AI' : 'TraceOS')}
            </div>
          )}
          <div 
            className="text-xs text-center mt-1"
            style={{ color: 'var(--color-sidebar-text-secondary, #94a3b8)' }}
          >
            {(() => {
              const ver = typeof window !== 'undefined' && (window as any).RUNTIME_CONFIG?.VERSION;
              return ver && ver !== 'unknown' ? `v${ver}` : null;
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

